import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from './Modal';
import ToDoModal from './ToDoModal';

const PhysicalAssetToDoList = ({ assetId, titleOverride, includeChildren = false, assetType = 'home' }) => {
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [categories, setCategories] = useState([]);

  const statusChoices = [
    { value: 'not_started', label: 'Not Started' },
    { value: 'in_progress', label: 'In-Progress' },
    { value: 'delayed_on_hold', label: 'Delayed/On-Hold' },
    { value: 'completed', label: 'Completed' }
  ];

  const fetchTodos = React.useCallback(async () => {
    if (!assetId) return;
    try {
      setLoading(true);
      const url = `/api/balance_sheet/assets/physical_assets/${assetId}/todo-items/${includeChildren ? '?include_children=true' : ''}`;
      const res = await axios.get(url);
      setTodos(res.data);
    } catch (err) {
      console.error('Error fetching todos:', err);
    } finally {
      setLoading(false);
    }
  }, [assetId, includeChildren]);

  const fetchCategories = React.useCallback(async () => {
    try {
      const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
      let fetchedCategories = [];
      if (res.data && Array.isArray(res.data)) {
          fetchedCategories = res.data;
      } else if (res.data && res.data.items) {
          const flat = [];
          const walk = (items) => {
              items.forEach(item => {
                  flat.push(item);
                  if (item.children) walk(item.children);
              });
          }
          walk(res.data.items);
          fetchedCategories = flat;
      }

      // Filter based on assetType
      if (assetType === 'vehicle') {
          setCategories(fetchedCategories.filter(c => 
            c.value === 'vehicle' || 
            c.value.startsWith('vehicle_') || 
            ['licensing_tags', 'repair', 'maintenance', 'enhancement', 'taxes', 'insurance', 'ticket_or_fine', 'asset_input', 'asset_storage', 'financing_interest', 'financing_principal'].includes(c.value)
          ));
      } else {
          // Default to home/property related
          setCategories(fetchedCategories.filter(c => 
            c.value === 'home' || 
            c.value.startsWith('home_') || 
            c.value === 'landscaping' ||
            c.value === 'plants' ||
            c.value === 'pest_control' ||
            ['repair', 'maintenance', 'enhancement', 'taxes', 'insurance', 'utilities', 'utilities_electric_gas', 'utilities_water', 'utilities_trash', 'utilities_sewer', 'utilities_internet', 'utilities_cable_tv', 'utilities_phone', 'utilities_other', 'financing_interest', 'financing_principal'].includes(c.value)
          ));
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [assetType]);

  useEffect(() => {
    if (assetId) {
      fetchTodos();
      fetchCategories();
    }
  }, [assetId, fetchTodos, fetchCategories]);

  const handleEdit = (todo) => {
    setEditingTodo(todo);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
      if (!window.confirm('Are you sure you want to delete this to-do item?')) return;
      try {
          await axios.delete(`/api/balance_sheet/assets/physical_assets/todo-items/${id}/`);
          fetchTodos();
      } catch (err) {
          console.error('Error deleting todo:', err);
      }
  };

  const handleStatusChange = async (id, newStatus) => {
      try {
          await axios.patch(`/api/balance_sheet/assets/physical_assets/todo-items/${id}/`, { status: newStatus });
          fetchTodos();
      } catch (err) {
          console.error('Error updating status:', err);
      }
  };

  const handleReorder = async (id, newPriority) => {
      try {
          await axios.post(`/api/balance_sheet/assets/physical_assets/todo-items/${id}/reorder/`, { priority: newPriority });
          fetchTodos();
      } catch (err) {
          console.error('Error reordering todo:', err);
      }
  };

  if (!assetId) return <div style={{ color: '#9ca3af', padding: '20px' }}>Loading To-Do List...</div>;

  if (loading) return <div style={{ color: '#9ca3af', padding: '20px' }}>Loading To-Do List...</div>;

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingTodo(null);
  };

  if (!assetId) return <div style={{ color: '#9ca3af', padding: '20px' }}>Loading To-Do List...</div>;
  if (loading) return <div style={{ color: '#9ca3af', padding: '20px' }}>Loading To-Do List...</div>;

  const groupedTodos = {
    'in_progress': todos.filter(t => t.status === 'in_progress'),
    'not_started': todos.filter(t => t.status === 'not_started'),
    'completed': todos.filter(t => t.status === 'completed'),
    'other': todos.filter(t => !['in_progress', 'not_started', 'completed'].includes(t.status))
  };

  const renderTable = (items, title) => {
    if (items.length === 0) return null;

    const groupExpectedCost = items.reduce((acc, t) => acc + (parseFloat(t.expected_cost) || 0), 0);
    const groupActualCost = items.reduce((acc, t) => acc + (parseFloat(t.actual_cost) || 0), 0);

    return (
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', borderBottom: '1px solid #374151', paddingBottom: '4px' }}>
          <h4 style={{ color: '#93c5fd', margin: 0 }}>{title}</h4>
          <div style={{ display: 'flex', gap: '20px', fontSize: '14px' }}>
            <span style={{ color: '#fbbf24' }}>Est: ${groupExpectedCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span style={{ color: '#10b981' }}>Act: ${groupActualCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-dark table-hover" style={{ fontSize: '13px', marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th style={{ width: '120px' }}>Status</th>
                <th>Title</th>
                <th>Category</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Compl. Date</th>
                <th>Est. Cost</th>
                <th>Act. Cost</th>
                <th style={{ width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((todo, index) => (
                <React.Fragment key={todo.id}>
                  <tr>
                    <td style={{ verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <button 
                          onClick={() => handleReorder(todo.id, todo.priority - 1)}
                          disabled={index === 0}
                          style={{ background: 'transparent', border: 'none', color: index === 0 ? '#4b5563' : '#93c5fd', cursor: index === 0 ? 'default' : 'pointer', padding: 0 }}
                          title="Move Up"
                        >▲</button>
                        <button 
                          onClick={() => handleReorder(todo.id, todo.priority + 1)}
                          disabled={index === items.length - 1}
                          style={{ background: 'transparent', border: 'none', color: index === items.length - 1 ? '#4b5563' : '#93c5fd', cursor: index === items.length - 1 ? 'default' : 'pointer', padding: 0 }}
                          title="Move Down"
                        >▼</button>
                      </div>
                    </td>
                    <td>
                      <select 
                        value={todo.status} 
                        onChange={(e) => handleStatusChange(todo.id, e.target.value)}
                        style={{ background: 'transparent', color: '#fff', border: 'none', fontSize: '13px', width: '100%' }}
                      >
                        {statusChoices.map(s => (
                          <option key={s.value} value={s.value} style={{ background: '#1f2937' }}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td style={{ fontWeight: '500' }}>{todo.title}</td>
                    <td>{(() => {
                        const val = todo.expense_category;
                        const displayCat = categories.find(c => c.value === val) || 
                                           categories.find(c => {
                                               const mapping = {
                                                   'repair': 'home_repair',
                                                   'maintenance': 'home_maintenance',
                                                   'enhancement': 'home_enhancement',
                                                   'taxes': 'home_tax_payment',
                                                   'insurance': 'home_insurance_payment',
                                                   'home_decor': 'home_decor',
                                                   'home_furniture': 'home_furniture',
                                                   'home_artwork': 'home_artwork',
                                                   'pest_control': 'home_pest_control',
                                                   'home_inspection': 'home_inspection',
                                                   'home_other': 'home_other'
                                               };
                                               return c.value === mapping[val];
                                           });
                        return displayCat?.label || val || '-';
                    })()}</td>
                    <td>{todo.expected_start_date || '-'}</td>
                    <td>{todo.expected_end_date || '-'}</td>
                    <td>{todo.completed_date || '-'}</td>
                    <td>{todo.expected_cost > 0 ? `$${parseFloat(todo.expected_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}</td>
                    <td style={{ color: todo.actual_cost > 0 ? '#10b981' : '#9ca3af' }}>
                      {todo.actual_cost > 0 ? `$${parseFloat(todo.actual_cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleEdit(todo)} className="btn btn-sm btn-outline-primary" style={{ padding: '2px 8px', fontSize: '11px' }}>Edit</button>
                        <button onClick={() => handleDelete(todo.id)} className="btn btn-sm btn-outline-danger" style={{ padding: '2px 8px', fontSize: '11px' }}>Delete</button>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="10" style={{ fontSize: '12px', color: '#9ca3af', borderTop: 'none', paddingTop: 0 }}>
                      <div style={{ display: 'flex', gap: '20px' }}>
                        <span><strong>Priority:</strong> {todo.priority}</span>
                        <span><strong>Description:</strong> {todo.description}</span>
                        {todo.next_steps && <span><strong>Next Steps:</strong> {todo.next_steps}</span>}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: '#e5e7eb', margin: 0 }}>{titleOverride || 'To-Do List'}</h3>
        <button onClick={() => setShowForm(true)} className="btn btn-sm btn-primary">+ To-Do Item</button>
      </div>

      <ToDoModal 
        isOpen={showForm} 
        onClose={handleCloseForm} 
        todo={editingTodo} 
        assetId={assetId} 
        assetType={assetType} 
        onSave={fetchTodos} 
      />

      {todos.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#9ca3af', padding: '40px', background: '#1f2937', borderRadius: '8px' }}>No to-do items found.</div>
      ) : (
        <>
          {renderTable(groupedTodos.in_progress, 'In-Progress')}
          {renderTable(groupedTodos.not_started, 'Not Started')}
          {renderTable(groupedTodos.completed, 'Completed')}
          {renderTable(groupedTodos.other, 'Other')}
        </>
      )}
    </div>
  );
};

export default PhysicalAssetToDoList;
